import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { DocumentScanResult, DocumentType } from '@/types/sale';

/**
 * POST /document/scan — send a KTP/SIM/STNK photo, get back a single-use scan
 * token + the server's trusted extraction (for cashier confirmation).
 *
 * The raw fields are returned once, for the operator to eyeball; the sale then
 * carries only `documentScanToken`, so the server re-reads the extraction and
 * document-OCR promos can't be gamed by a spoofed client payload.
 *
 * @throws ApiError — INVALID_DOCUMENT_TYPE / IMAGE_REQUIRED (400),
 *   SCAN_STORE_UNAVAILABLE (503), or an OCR-vendor failure (502).
 */
export async function scanDocument(
  image: File,
  documentType: DocumentType,
): Promise<DocumentScanResult> {
  const form = new FormData();
  form.append('image', image);
  form.append('documentType', documentType);
  try {
    const res = await http.post<ApiEnvelope<DocumentScanResult>>('/document/scan', form, {
      // Let the browser set the multipart boundary — override the client's JSON default.
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
